<template>
    <div>
        <v-card-title>
            Управляемые системы
            <v-spacer></v-spacer>
            <v-btn icon class="mr-2" @click="fetch()"><v-icon>mdi-refresh</v-icon></v-btn>
            <v-btn color="primary" @click="openEdit(null)">
                <v-icon left>mdi-plus</v-icon>
                Добавить
            </v-btn>
        </v-card-title>

        <v-data-table :headers="headers" :items="systems" :loading="loading" disable-pagination hide-default-footer
                      no-data-text="Систем пока нет">
            <template v-slot:[`item.name`]="{ item }">
                <div>{{ item.name }}<span v-if="item.planName" class="text--secondary"> · {{ item.planName }}</span></div>
                <a :href="item.url" target="_blank" rel="noopener" class="font-size__small">{{ item.url }}</a>
            </template>
            <template v-slot:[`item.balance`]="{ item }">{{ money(item.balance) }}</template>
            <template v-slot:[`item.dailyRate`]="{ item }">{{ money(item.dailyRate) }}</template>
            <template v-slot:[`item.paidUntil`]="{ item }">{{ formatDate(item.paidUntil) || '—' }}</template>
            <template v-slot:[`item.status`]="{ item }">
                <v-chip v-if="!item.enabled" small>Не управляется</v-chip>
                <v-chip v-else-if="item.status === 'BLOCKED'" small color="red" text-color="white">Заблокирована</v-chip>
                <v-chip v-else small color="green" text-color="white">Активна</v-chip>
            </template>
            <template v-slot:[`item.sync`]="{ item }">
                <v-tooltip bottom max-width="400">
                    <template v-slot:activator="{ on }">
                        <v-icon v-on="on" :color="item.lastSyncError ? 'red' : (item.lastSyncAt ? 'green' : 'grey')">
                            {{ item.lastSyncError ? 'mdi-sync-alert' : 'mdi-sync' }}
                        </v-icon>
                    </template>
                    <span v-if="item.lastSyncError">Ошибка: {{ item.lastSyncError }}</span>
                    <span v-else-if="item.lastSyncAt">Синхронизировано {{ formatDateTime(item.lastSyncAt) }}</span>
                    <span v-else>Ещё не синхронизировалась</span>
                </v-tooltip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-btn small color="green" class="mr-1" @click="openMoney(item, 'topUp')">Пополнить</v-btn>
                <v-menu offset-y left>
                    <template v-slot:activator="{ on }">
                        <v-btn icon small v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item @click="openEdit(item)">
                            <v-list-item-title>Редактировать</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openMoney(item, 'adjust')">
                            <v-list-item-title>Корректировка баланса</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="openHistory(item)">
                            <v-list-item-title>История операций</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="sync(item)">
                            <v-list-item-title>Синхронизировать сейчас</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>

        <!-- Create / edit -->
        <v-dialog v-model="edit.open" max-width="520">
            <v-card>
                <v-card-title>{{ edit.id ? 'Редактирование системы' : 'Новая система' }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="edit.form.name" label="Название"></v-text-field>
                    <v-text-field v-model="edit.form.url" label="URL бэкенда"
                                  hint="Например https://crm.client.ru — без /api" persistent-hint></v-text-field>
                    <v-text-field v-model="edit.form.planName" label="Тариф" counter="100" class="mt-4"
                                  hint="Видят админы клиента: «Доступ «Энтерпрайз»». Можно оставить пустым"
                                  persistent-hint></v-text-field>
                    <v-text-field v-model="edit.form.dailyRate" label="Стоимость в день, ₽" type="number" min="0"
                                  class="mt-4"></v-text-field>
                    <v-switch v-model="edit.form.enabled" label="Управлять системой (списания и блокировка)"
                              hint="При выключении система разблокируется и больше не списывается" persistent-hint></v-switch>
                    <v-alert v-if="edit.error" type="error" dense text class="mt-4 mb-0">{{ edit.error }}</v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="edit.open = false">Отмена</v-btn>
                    <v-btn color="primary" :loading="edit.busy" @click="saveEdit()">Сохранить</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Top-up / adjust -->
        <v-dialog v-model="moneyDialog.open" max-width="440">
            <v-card v-if="moneyDialog.system">
                <v-card-title>
                    {{ moneyDialog.mode === 'topUp' ? 'Пополнение' : 'Корректировка' }} — {{ moneyDialog.system.name }}
                </v-card-title>
                <v-card-text>
                    <div class="mb-4">Текущий баланс: <b>{{ money(moneyDialog.system.balance) }}</b></div>
                    <v-text-field v-model="moneyDialog.amount" label="Сумма, ₽" type="number"
                                  :hint="moneyDialog.mode === 'adjust' ? 'Отрицательная сумма уменьшает баланс' : ''"
                                  persistent-hint autofocus></v-text-field>
                    <v-text-field v-model="moneyDialog.comment" label="Комментарий" class="mt-2"></v-text-field>
                    <v-alert v-if="moneyDialog.error" type="error" dense text class="mb-0">{{ moneyDialog.error }}</v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="moneyDialog.open = false">Отмена</v-btn>
                    <v-btn color="primary" :loading="moneyDialog.busy" @click="saveMoney()">Сохранить</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- History -->
        <v-dialog v-model="history.open" max-width="900">
            <v-card v-if="history.system">
                <v-card-title>
                    История операций — {{ history.system.name }}
                    <v-spacer></v-spacer>
                    <v-btn icon @click="history.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-data-table :headers="historyHeaders" :items="history.items" :loading="history.loading"
                              :server-items-length="history.total" :options.sync="history.options"
                              :footer-props="{'items-per-page-options': [20, 50, 100]}" no-data-text="Операций нет">
                    <template v-slot:[`item.createdAt`]="{ item }">{{ formatDateTime(item.createdAt) }}</template>
                    <template v-slot:[`item.type`]="{ item }">
                        {{ transactionTypes[item.type] }}
                        <span v-if="item.chargeDate" class="font-size__small"> за {{ formatDate(item.chargeDate) }}</span>
                    </template>
                    <template v-slot:[`item.amount`]="{ item }">
                        <span :class="item.amount < 0 ? 'red--text' : 'green--text'">{{ money(item.amount) }}</span>
                    </template>
                    <template v-slot:[`item.balanceAfter`]="{ item }">{{ money(item.balanceAfter) }}</template>
                    <template v-slot:[`item.createdByName`]="{ item }">{{ item.createdByName || 'Система' }}</template>
                </v-data-table>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import SubscriptionService from "@/_services/SubscriptionService";

const emptyForm = () => ({name: '', url: '', planName: '', dailyRate: null, enabled: true});

export default {
    name: "ManagedSystems",
    data() {
        return {
            loading: false,
            systems: [],
            headers: [
                {text: 'Система', value: 'name'},
                {text: 'Баланс', value: 'balance', align: 'end'},
                {text: '₽ / день', value: 'dailyRate', align: 'end'},
                {text: 'Оплачено до', value: 'paidUntil'},
                {text: 'Статус', value: 'status'},
                {text: 'Синхр.', value: 'sync', sortable: false, align: 'center'},
                {text: '', value: 'actions', sortable: false, align: 'end'},
            ],
            historyHeaders: [
                {text: 'Дата', value: 'createdAt', sortable: false},
                {text: 'Операция', value: 'type', sortable: false},
                {text: 'Сумма', value: 'amount', sortable: false, align: 'end'},
                {text: 'Баланс после', value: 'balanceAfter', sortable: false, align: 'end'},
                {text: 'Комментарий', value: 'comment', sortable: false},
                {text: 'Кто', value: 'createdByName', sortable: false},
            ],
            transactionTypes: {TOPUP: 'Пополнение', DAILY_CHARGE: 'Списание', ADJUSTMENT: 'Корректировка'},
            edit: {open: false, id: null, form: emptyForm(), busy: false, error: null},
            moneyDialog: {open: false, mode: 'topUp', system: null, amount: null, comment: '', busy: false, error: null},
            history: {open: false, system: null, items: [], total: 0, loading: false, options: {page: 1, itemsPerPage: 20}},
        }
    },
    watch: {
        'history.options': {
            deep: true,
            handler() {
                if (this.history.open) {
                    this.fetchHistory();
                }
            }
        }
    },
    methods: {
        fetch() {
            this.loading = true;
            SubscriptionService.getManagedSystems()
                .then(data => this.systems = data)
                .finally(() => this.loading = false);
        },
        replace(updated) {
            const index = this.systems.findIndex(system => system.id === updated.id);
            if (index >= 0) {
                this.$set(this.systems, index, updated);
            } else {
                this.systems.push(updated);
            }
        },
        errorMessage(error) {
            const data = error.response && error.response.data;
            if (data && data.errors && data.errors.length) {
                return data.errors.map(e => (e.field ? e.field + ': ' : '') + e.defaultMessage).join('; ');
            }
            return (data && data.message) || this.$helper.showError(error);
        },
        openEdit(system) {
            this.edit = {
                open: true,
                id: system ? system.id : null,
                form: system
                    ? {name: system.name, url: system.url, planName: system.planName || '', dailyRate: system.dailyRate, enabled: system.enabled}
                    : emptyForm(),
                busy: false,
                error: null
            };
        },
        saveEdit() {
            this.edit.busy = true;
            this.edit.error = null;
            const request = this.edit.id
                ? SubscriptionService.updateManagedSystem(this.edit.id, this.edit.form)
                : SubscriptionService.createManagedSystem(this.edit.form);
            request
                .then(data => {
                    this.replace(data);
                    this.edit.open = false;
                })
                .catch(error => this.edit.error = this.errorMessage(error))
                .finally(() => this.edit.busy = false);
        },
        openMoney(system, mode) {
            this.moneyDialog = {open: true, mode, system, amount: null, comment: '', busy: false, error: null};
        },
        saveMoney() {
            const {mode, system, amount, comment} = this.moneyDialog;
            this.moneyDialog.busy = true;
            this.moneyDialog.error = null;
            const body = {amount, comment};
            (mode === 'topUp' ? SubscriptionService.topUp(system.id, body) : SubscriptionService.adjust(system.id, body))
                .then(data => {
                    this.replace(data);
                    this.moneyDialog.open = false;
                    // The push to the client runs after commit - refresh to show its result.
                    setTimeout(() => this.fetch(), 1500);
                })
                .catch(error => this.moneyDialog.error = this.errorMessage(error))
                .finally(() => this.moneyDialog.busy = false);
        },
        sync(system) {
            SubscriptionService.sync(system.id)
                .then(data => {
                    this.replace(data);
                    if (data.lastSyncError) {
                        this.flashError(`Синхронизация не удалась: ${data.lastSyncError}`);
                    } else {
                        this.flashSuccess('Синхронизировано');
                    }
                })
                .catch(error => this.flashError(this.errorMessage(error)));
        },
        openHistory(system) {
            // Replacing `options` triggers the watcher, which does the fetch.
            this.history = {open: true, system, items: [], total: 0, loading: false, options: {page: 1, itemsPerPage: 20}};
        },
        fetchHistory() {
            const {system, options} = this.history;
            this.history.loading = true;
            SubscriptionService.getTransactions(system.id, options.page - 1, options.itemsPerPage)
                .then(data => {
                    this.history.items = data.content;
                    this.history.total = data.totalElements;
                })
                .finally(() => this.history.loading = false);
        },
        money(value) {
            return value === null || value === undefined ? '—'
                : Number(value).toLocaleString('ru-RU', {minimumFractionDigits: 0, maximumFractionDigits: 2}) + ' ₽';
        },
        formatDate(date) {
            return date ? date.split('-').reverse().join('.') : '';
        },
        formatDateTime(value) {
            return value ? new Date(value).toLocaleString('ru-RU') : '';
        }
    },
    created() {
        this.fetch();
    }
};
</script>
